import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { KeyRound, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ClaveCard } from '@/components/clientes/ClaveCard'
import { PinInput } from '@/components/clientes/PinInput'
import { CLAVE_FISCAL_VACIA, puedeVerClaveInscripcion, type ClaveFiscalValores } from '@/core/lib/inscripcion'
import { ORGANISMOS, sanitizeVaultError } from '@/lib/cajaFuerte'
import { usePermissions } from '@/hooks/usePermissions'
import { clavesInscripcionKey, useVault, type ClaveInscripcion } from '@/hooks/useVault'

type Vault = ReturnType<typeof useVault>

/** Organismos de la Caja Fuerte del cliente, sin «Otro», que ahí abre un texto libre. */
const ORGANISMOS_CLAVE = ORGANISMOS.filter((organismo) => organismo !== 'Otro')

/** Muestra `children` con la Caja Fuerte abierta. Si está cerrada pide el PIN, y si el usuario no tiene PIN lo avisa. */
export function ConCajaFuerteAbierta({ vault, para, children }: { vault: Vault; para: string; children: ReactNode }) {
  const [error, setError] = useState(false)
  const [verificando, setVerificando] = useState(false)
  const { hasPin, checkPinStatus, verificarPin } = vault

  useEffect(() => {
    if (hasPin === null) void checkPinStatus()
  }, [hasPin, checkPinStatus])

  if (hasPin === null || verificando) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  if (!hasPin) {
    return (
      <p className="text-sm text-muted-foreground">
        Para {para} hace falta el PIN de la Caja Fuerte, que se configura desde la Caja Fuerte de un cliente.
      </p>
    )
  }
  if (!vault.desbloqueado) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Ingresá tu PIN de la Caja Fuerte para {para}.</p>
        <PinInput
          error={error}
          onComplete={async (pin) => {
            setError(false)
            setVerificando(true)
            const valido = await verificarPin(pin)
            setVerificando(false)
            setError(!valido)
          }}
        />
        {error && <p className="text-sm text-destructive">PIN incorrecto</p>}
      </div>
    )
  }
  return <>{children}</>
}

/** Organismo, usuario y clave de la clave fiscal de una inscripción. */
export function CamposClaveFiscal({
  valores,
  onChange,
}: {
  valores: ClaveFiscalValores
  onChange: (valores: ClaveFiscalValores) => void
}) {
  return (
    <div className="grid gap-3">
      <div className="space-y-2">
        <Label htmlFor="clave-fiscal-organismo">Organismo</Label>
        <Select value={valores.organismo} onValueChange={(organismo) => onChange({ ...valores, organismo })}>
          <SelectTrigger id="clave-fiscal-organismo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORGANISMOS_CLAVE.map((organismo) => (
              <SelectItem key={organismo} value={organismo}>
                {organismo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="clave-fiscal-usuario">Usuario</Label>
        <Input
          id="clave-fiscal-usuario"
          placeholder="Si lo dejás vacío, el CUIT"
          value={valores.usuario}
          onChange={(e) => onChange({ ...valores, usuario: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="clave-fiscal-clave">Clave</Label>
        <Input
          id="clave-fiscal-clave"
          type="password"
          autoComplete="new-password"
          value={valores.clave}
          onChange={(e) => onChange({ ...valores, clave: e.target.value })}
        />
      </div>
    </div>
  )
}

/** Claves descifradas de la tarea. Viven en el estado del componente y no en el cache de queries. */
function ClavesDeLaInscripcion({ vault, tareaId }: { vault: Vault; tareaId: string }) {
  const { obtenerClavesInscripcion } = vault
  const [claves, setClaves] = useState<ClaveInscripcion[] | null>(null)

  useEffect(() => {
    let vigente = true
    obtenerClavesInscripcion(tareaId)
      .then((leidas) => vigente && setClaves(leidas))
      .catch((err) => {
        if (!vigente) return
        setClaves([])
        toast.error(sanitizeVaultError(err, 'La clave fiscal de la inscripción no se pudo leer.'))
      })
    return () => {
      vigente = false
    }
  }, [obtenerClavesInscripcion, tareaId])

  if (claves === null) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  return (
    <div className="space-y-2">
      {claves.map((clave) => (
        <ClaveCard key={clave.id} clave={{ ...clave, notas: null }} onEditar={() => {}} onEliminar={() => {}} editable={false} />
      ))}
    </div>
  )
}

/** Aviso de una clave de la inscripción que no pasó a la Caja Fuerte del cliente, con el botón para pasarla. */
function PasarClaveALaCajaFuerte({ vault, tareaId, clienteId }: { vault: Vault; tareaId: string; clienteId: string }) {
  const [enCurso, setEnCurso] = useState(false)

  const pasar = async () => {
    setEnCurso(true)
    try {
      await vault.transferirClavesInscripcion(tareaId, clienteId)
      toast.success('La clave fiscal pasó a la Caja Fuerte del cliente.')
    } catch (err) {
      toast.error(sanitizeVaultError(err, 'La clave fiscal no se pudo pasar a la Caja Fuerte del cliente.'))
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-violet-200 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950">
      <p className="text-sm">La clave fiscal de la inscripción no pasó a la Caja Fuerte del cliente.</p>
      <Button size="sm" disabled={enCurso} onClick={pasar}>
        Pasar la clave a la Caja Fuerte
      </Button>
    </div>
  )
}

interface TareaConClave {
  id: string
  cuit: string | null
  cliente_id: string | null
  asignado_a: string | null
}

/**
 * Clave fiscal en el detalle de una inscripción. Sin cliente, dice qué organismos tienen clave, la
 * muestra con PIN y la deja cargar con `crearCliente`. Con cliente, la clave ya pasó a su Caja
 * Fuerte; si quedó alguna sin pasar, ofrece pasarla a quien tiene `crearCliente`. No se muestra a
 * quien no puede ver la clave.
 */
export function ClaveFiscalDeInscripcion({ tarea }: { tarea: TareaConClave }) {
  const vault = useVault()
  const { puede, rolMultiTenant, emailUsuario } = usePermissions()
  const puedeVer = puedeVerClaveInscripcion(rolMultiTenant, tarea.asignado_a, emailUsuario)

  const { data: organismos = [], isError, isPending } = useQuery({
    queryKey: clavesInscripcionKey(tarea.id),
    queryFn: () => vault.organismosConClaveInscripcion(tarea.id),
    enabled: puedeVer,
  })

  if (!puedeVer || isError || isPending) return null
  if (!tarea.cliente_id) {
    return <ClaveFiscalSinCliente vault={vault} tarea={tarea} organismos={organismos} puedeCargar={puede.crearCliente} />
  }
  const pendiente = organismos.length > 0 && puede.crearCliente
  return pendiente ? <PasarClaveALaCajaFuerte vault={vault} tareaId={tarea.id} clienteId={tarea.cliente_id} /> : null
}

/** Clave fiscal de una inscripción sin cliente: qué organismos tienen clave, verla con PIN y cargarla con `puedeCargar`. */
function ClaveFiscalSinCliente({
  vault,
  tarea,
  organismos,
  puedeCargar,
}: {
  vault: Vault
  tarea: TareaConClave
  organismos: string[]
  puedeCargar: boolean
}) {
  const [modo, setModo] = useState<'resumen' | 'ver' | 'cargar'>('resumen')
  const [valores, setValores] = useState(CLAVE_FISCAL_VACIA)
  const [enCurso, setEnCurso] = useState(false)

  const guardar = async () => {
    setEnCurso(true)
    try {
      await vault.guardarClaveInscripcion(tarea.id, {
        organismo: valores.organismo,
        usuario: valores.usuario.trim() || tarea.cuit || '',
        clave: valores.clave,
      })
      toast.success('Clave fiscal guardada')
      setModo('resumen')
    } catch (err) {
      toast.error(sanitizeVaultError(err, 'La clave fiscal no se pudo guardar.'))
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">Clave fiscal</h4>
      {modo === 'resumen' && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm">{organismos.length > 0 ? `Cargada: ${organismos.join(', ')}` : 'Sin clave cargada'}</p>
          {organismos.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setModo('ver')}>
              <KeyRound className="h-4 w-4 mr-1" />
              Ver clave
            </Button>
          )}
          {puedeCargar && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setValores(CLAVE_FISCAL_VACIA)
                setModo('cargar')
              }}
            >
              Cargar clave
            </Button>
          )}
        </div>
      )}
      {modo === 'ver' && (
        <>
          <ConCajaFuerteAbierta vault={vault} para="ver la clave">
            <ClavesDeLaInscripcion vault={vault} tareaId={tarea.id} />
          </ConCajaFuerteAbierta>
          <Button size="sm" variant="ghost" onClick={() => setModo('resumen')}>
            Ocultar
          </Button>
        </>
      )}
      {modo === 'cargar' && (
        <>
          <ConCajaFuerteAbierta vault={vault} para="cargar la clave">
            <CamposClaveFiscal valores={valores} onChange={setValores} />
          </ConCajaFuerteAbierta>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setModo('resumen')}>
              Cancelar
            </Button>
            {vault.desbloqueado && (
              <Button size="sm" disabled={!valores.clave || enCurso} onClick={guardar}>
                {enCurso && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Guardar clave
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
